<script setup lang="ts">
import { PV } from "@/widgets";
import { syncRef, whenever } from "@vueuse/core";
import { sortBy } from "es-toolkit";
import { computed, ref, watch, watchEffect } from "vue";
import { defineActions, defineState } from "../common";
import { Buttons } from "../icons";
import { FormExt, FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = number;
interface PropsType extends BaseProps<ModelType> {
    units: {
        label: string;
        value: number;
    }[];
    formId?: string;
    formSubmit?: boolean;
    placeholder?: string;
    plain?: boolean;
    min?: number;
    max?: number;
    zeroAsInfinite?: boolean;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    plain: false,
    formSubmit: false,
    min: 0,
    max: Number.MAX_SAFE_INTEGER,
    zeroAsInfinite: false,
});

const slots = defineSlots<{
    icon?: () => unknown;
}>();

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);
const infiniteRef = ref("∞");

const unitsAsc = computed(() => sortBy(props.units, [(u) => u.value]));
const unitsDesc = computed(() => sortBy(props.units, [(u) => -u.value]));
const unitValueLabels = computed(() => new Map(props.units.map((u) => [u.value, u.label])));

const unitModel = ref<number>(unitsAsc.value[0].value);
const valueModel = ref<number | undefined>(model.value);

syncRef(model, valueModel, {
    transform: {
        ltr: (left) => {
            if (left === undefined) {
                return undefined;
            }

            return left / unitModel.value;
        },
        rtl: (right) => {
            if (right === undefined) {
                return undefined;
            }

            return right * unitModel.value;
        },
    },
});

if (!state.editMode) {
    whenever(
        () => model.value !== undefined,
        () => {
            const v = model.value!;
            const asc = unitsAsc.value;
            const desc = unitsDesc.value;

            if (v === 0) {
                unitModel.value = asc[0].value;
                return;
            }

            for (const unit of desc) {
                if (v % unit.value === 0) {
                    unitModel.value = unit.value;
                    valueModel.value = v / unit.value;
                    break;
                }
            }
        },
        { once: true, immediate: true },
    );
}

watch(unitModel, (unit) => {
    if (valueModel.value === undefined) return;
    model.value = valueModel.value * unit;
});

watchEffect(() => {
    if (model.value === null) {
        model.value = undefined;
    } else if (model.value !== undefined) {
        if (props.max !== undefined && model.value > props.max) {
            unitModel.value = unitsAsc.value[0].value;
            model.value = props.max;
        } else if (props.min !== undefined && model.value < props.min) {
            unitModel.value = unitsAsc.value[0].value;
            model.value = props.min;
        }
    }
});

async function catchSubmit() {
    if (props.formSubmit === true) {
        if (props.formId === undefined) return;
        document.forms.namedItem(props.formId)?.requestSubmit();
    } else {
        await actions.onSubmit();
    }
}
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :param="props" :state="state" />

        <template v-if="!state.editMode">
            <PV.IconField>
                <PV.InputIcon v-if="slots.icon !== undefined" class="-mt-2.5!">
                    <slot name="icon"></slot>
                </PV.InputIcon>
                <template v-if="model === undefined">
                    <PV.InputText
                        v-if="props.placeholder === '∞'"
                        class="rns-input-placeholder-infinite"
                        disabled
                        fluid
                        :value="props.placeholder"
                    />
                    <PV.InputText v-else class="rns-input-placeholder" disabled fluid :value="props.placeholder" />
                </template>
                <template v-else-if="props.zeroAsInfinite">
                    <PV.InputNumber
                        v-if="model > 0"
                        v-model="valueModel"
                        class="rns-input-static"
                        disabled
                        fluid
                        locale="id-ID"
                        :maxFractionDigits="0"
                        :suffix="' ' + unitValueLabels.get(unitModel)"
                        :useGrouping="props.plain === false"
                    />
                    <PV.InputText v-else v-model="infiniteRef" class="rns-input-static-infinite" disabled fluid />
                </template>
                <PV.InputNumber
                    v-else
                    v-model="valueModel"
                    class="rns-input-static"
                    disabled
                    fluid
                    locale="id-ID"
                    :maxFractionDigits="0"
                    :suffix="' ' + unitValueLabels.get(unitModel)"
                    :useGrouping="props.plain === false"
                />
                <FormExt :actions="actions" :state="state" />
            </PV.IconField>
        </template>
        <template v-else>
            <div class="inline-flex flex-row">
                <PV.IconField class="w-9/12">
                    <PV.InputIcon v-if="slots.icon !== undefined" class="-mt-2.5!">
                        <slot name="icon"></slot>
                    </PV.InputIcon>

                    <PV.InputNumber
                        v-model="valueModel"
                        class="rns-input-value"
                        :disabled="props.disabled"
                        fluid
                        :form="props.formId"
                        :inputId="state.id"
                        :invalid="state.hasError"
                        locale="id-ID"
                        :maxFractionDigits="0"
                        :placeholder="props.placeholder ?? `Masukkan ${label}`"
                        :required="props.required"
                        :useGrouping="props.plain === false"
                        @change="actions.onValidate"
                        @keydown.enter.prevent="catchSubmit"
                    />

                    <Buttons.ExtReset v-if="state.clearable && state.hasValue" @click="actions.onReset" />
                </PV.IconField>
                <PV.Select
                    v-model="unitModel"
                    class="w-3/12 rns-input-unit"
                    optionLabel="label"
                    :options="props.units"
                    optionValue="value"
                />
            </div>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-input-static :deep(.p-inputnumber-input),
.rns-input-placeholder {
    @apply bg-transparent! shadow-none! px-1! pt-2.25!;
    @apply rns-g-static-input;
}

.rns-input-static :deep(.p-inputnumber-input) {
    @apply text-gray-700!;
}

.rns-input-placeholder {
    @apply text-gray-500!;
}

.rns-input-value :deep(.p-inputnumber-input) {
    @apply rounded-tr-none rounded-br-none;
}

.rns-input-unit {
    @apply rounded-tl-none rounded-bl-none;
}

.rns-input-static-infinite,
.rns-input-placeholder-infinite {
    @apply bg-transparent! shadow-none! px-1! pt-1.25!;
    @apply rns-g-static-input font-bold text-xl;
}

.rns-input-static-infinite {
    @apply text-gray-700!;
}

.rns-input-placeholder-infinite {
    @apply text-gray-500!;
}
</style>
